/**
 * Plain-language help for Marketplace settings (Features tab).
 * Shown in info bubbles on Settings → Features → Marketplace.
 */

/**
 * @typedef {import('./pricingSettingsHelp.js').PricingFieldHelp} MarketplaceFieldHelp
 */

/** @type {Record<string, MarketplaceFieldHelp>} */
export const MARKETPLACE_FIELD_HELP = Object.freeze({
  "marketplace.experiment.startAt": {
    title: "Experiment cohort start",
    summary:
      "UTC timestamp when post-deposit ProviderOpportunity auto-create opens for the Wave 1.5 experiment.",
    details: [
      "Empty = cohort closed — no auto-create after deposit verification.",
      "Set an ISO UTC value (e.g. 2026-08-08T12:00:00.000Z) and save to open the cohort.",
      "Useful on dev/staging until ops deliberately opens the experiment.",
    ],
    example:
      "2026-08-08T12:00:00.000Z — orders with verified deposits after this time get a ProviderOpportunity.",
    whenApplies: "After deposit verification on marketplace-eligible orders.",
  },
  "marketplace.experiment.minFundedOpportunities": {
    title: "Min funded opportunities",
    summary:
      "How many funded ProviderOpportunities are needed before the Wave 1.5 confidence sample is met.",
    details: [
      "Used by the experiment dashboard to track progress toward the funded-opportunity gate.",
      "Does not block individual orders — it is a reporting/confidence threshold.",
    ],
    example: "100 — dashboard shows confidence sample met once 100 opportunities are funded.",
    whenApplies: "Experiment metrics and cohort confidence reporting.",
  },
  "marketplace.experiment.minCompletedMoves": {
    title: "Min completed moves",
    summary:
      "How many fulfilled ProviderOpportunities are needed before the Wave 1.5 confidence sample is met.",
    details: [
      "Counts opportunities that reached a completed/fulfilled outcome.",
      "Pairs with min funded opportunities for end-to-end experiment confidence.",
    ],
    example: "70 — confidence sample requires both funded and completed thresholds.",
    whenApplies: "Experiment metrics and cohort confidence reporting.",
  },
  "marketplace.leadTime.sharedLoadHours": {
    title: "Shared-load selection close",
    summary:
      "Hours before collection when shared-load provider selection closes.",
    details: [
      "Sets selectionDeadlineAt on shared-load ProviderOpportunities.",
      "Ops must pick a provider before this window closes.",
    ],
    example: "48 — selection closes 48 hours before collection on shared loads.",
    whenApplies: "Shared-load ProviderOpportunity creation and deadline worker.",
  },
  "marketplace.leadTime.dedicatedLoadHours": {
    title: "Dedicated-load selection close",
    summary:
      "Hours before collection when dedicated/exclusive-load provider selection closes.",
    details: [
      "Typically longer than shared-load because exclusive moves need more lead time.",
      "Sets selectionDeadlineAt on dedicated/exclusive ProviderOpportunities.",
    ],
    example: "72 — selection closes 72 hours before collection on dedicated loads.",
    whenApplies: "Dedicated/exclusive ProviderOpportunity creation and deadline worker.",
  },
  "marketplace.ops.notifyOnInterest": {
    title: "Notify on provider response",
    summary:
      "Email admin and create an in-app notification when a provider accepts (interested) or rejects (declined) a job opportunity.",
    details: [
      "Recipients use admin.email system setting and ADMIN_EMAIL env fallback.",
      "Fail-open — a notification error does not block the provider response.",
      "Interested alerts are high priority (action required); declined/pass are informational.",
    ],
    example: "ON — ops gets an email as soon as a provider taps Interested or Declined.",
    whenApplies: "Provider invitation response flow (interested, declined, pass).",
  },
  "marketplace.ops.deadlineWarningHours": {
    title: "Deadline warning lead time",
    summary:
      "How many hours before selectionDeadlineAt to warn ops that the window is closing.",
    details: [
      "One warning per ProviderOpportunity (opsDeadlineWarningSentAt prevents repeats).",
      "Requires ENABLE_PROVIDER_OPPORTUNITY_DEADLINE_WARNING_WORKER=true on the backend.",
    ],
    example: "24 — ops is warned 24 hours before each open opportunity deadline.",
    whenApplies: "Background deadline warning worker (when enabled).",
  },
  "marketplace.providerOnboarding.enabled": {
    title: "Provider onboarding gate",
    summary:
      "Turns on Wave 1.6 eligibility (vetted + e-signed) before providers can take jobs.",
    details: [
      "Keep OFF until staging walkthrough is verified.",
      "Does not create Partners at intake — Partner create remains at verify.",
    ],
    example: "OFF until staging verify, then ON for production cutover.",
    whenApplies: "Invite lists, Interested, and bid eligibility.",
  },
  "marketplace.providerApply.googleFormUrl": {
    title: "Provider Google Form URL",
    summary:
      "Public form link emailed after short intake. Paste here — no code deploy.",
    details: [
      "Admin path: Settings → Features → Provider onboarding.",
      "Leave empty until the Google Form exists; email copy will say the team will send the form shortly.",
      "Never commit a docs.google.com URL into the repo.",
    ],
    example: "https://docs.google.com/forms/d/e/…/viewform",
    whenApplies: "Provider intake follow-up email (W16-E01-003).",
  },
  "marketplace.providerApply.googleFormPrefill": {
    title: "Provider Google Form prefill map",
    summary:
      "Maps intake field names to Google Form entry.XXX query params for URL prefill.",
    details: [
      "Keys include fullName, email, phone, city, vehicleType, idNumber, idDocumentType, applicantType, companyName, isOwner, coveragePreference, drivingModel, licenceNumber, driversSummary.",
      "Get entry.XXX ids from the form’s prefilled-link helper in Google Forms.",
    ],
    example:
      '{"email":"entry.123456","idDocumentType":"entry.234567","driversSummary":"entry.345678"}',
    whenApplies: "buildGoogleFormUrl when sending intake follow-up email.",
  },
});

/** @type {Record<string, { summary: string, details?: string[] }>} */
export const MARKETPLACE_SECTION_HELP = Object.freeze({
  experiment: {
    summary:
      "Gate ProviderOpportunity auto-create and track Wave 1.5 experiment confidence thresholds.",
    details: [
      "Start with cohort startAt — without it, no auto-create runs after deposit verification.",
      "Confidence counts feed the experiment dashboard; they do not block individual orders.",
    ],
  },
  leadTime: {
    summary:
      "Define how long ops has to select a provider before collection.",
    details: [
      "Shared loads usually close sooner than dedicated/exclusive moves.",
      "Deadlines drive the ops deadline-warning notifications when that worker is enabled.",
    ],
  },
  opsAlerts: {
    summary:
      "Keep ops informed when providers respond or selection deadlines approach.",
    details: [
      "Interest and decline alerts fire immediately on provider response.",
      "Deadline warnings are sent once per opportunity by the backend worker.",
    ],
  },
  providerOnboarding: {
    summary:
      "Wave 1.6 provider opt-in: Google Form URL + prefill map + onboarding gate.",
    details: [
      "Paste the form URL and entry.XXX map here so intake emails prefill without a deploy.",
      "Keep the onboarding gate false until staging is verified.",
    ],
  },
});

/**
 * @param {string} key
 * @returns {MarketplaceFieldHelp|undefined}
 */
export const getMarketplaceFieldHelp = (key) => MARKETPLACE_FIELD_HELP[key];

/**
 * @param {string} sectionKey
 * @returns {{ summary: string, details?: string[] }|undefined}
 */
export const getMarketplaceSectionHelp = (sectionKey) =>
  MARKETPLACE_SECTION_HELP[sectionKey];
